import {
    useInfiniteQuery,
    useQueryClient,
    useQuery,
    QueryKey,
    QueryOptions,
    QueryResult,
    InfiniteData,
} from '@eika-infrastruktur/react-query';
import { RequestExceptionWithResponse } from '@eika-infrastruktur/request';

import { SearchPayload } from '../components/transaction/TransactionFilter/transactionFilterTypes';
import type { AccountId, TransactionDetails, TransactionId, TransactionList } from '../generated-types';
import { getTransaction, getTransactions } from '../services/accountService';

const useTransactions = (accountId?: AccountId, params?: SearchPayload) => {
    return useInfiniteQuery<
        TransactionList,
        RequestExceptionWithResponse,
        InfiniteData<TransactionList>,
        QueryKey,
        string | undefined
    >({
        queryKey: ['transactions', accountId, params],
        queryFn: (args) => getTransactions(accountId!, params, args.pageParam),
        getNextPageParam: (lastPage) => lastPage?.nextReference,
        enabled: !!accountId,
        initialPageParam: undefined,
    });
};

export const useUpdateTransactions = () => {
    const queryClient = useQueryClient();

    return (accountIds: AccountId[] = []) => {
        return queryClient.invalidateQueries({
            predicate: (query) => {
                if (query.queryKey[0] === 'transactions' && query.queryKey[1]) {
                    const accountId = query.queryKey[1] as AccountId;
                    return accountIds.some((a) => a.value === accountId.value);
                }
                return query.queryKey[0] === 'transactions';
            },
        });
    };
};

export const useTransactionDetails = (
    accountId?: AccountId,
    transactionId?: TransactionId,
    options: Partial<QueryOptions<TransactionDetails>> = {},
): QueryResult<TransactionDetails> =>
    useQuery({
        ...options,
        queryKey: ['transaction', accountId, transactionId],
        queryFn: () => getTransaction(accountId!, transactionId!),
        enabled: !!accountId && !!transactionId,
        staleTime: Infinity,
    });

export default useTransactions;
